import { useMemo } from 'react';
import type { DailyAggregate } from '@etiquetachef/types';

export type AnomalyKind = 'MRR_DROP' | 'SALES_DROP' | 'NET_NEW_NEGATIVE_3D';

export type Anomaly = {
  kind: AnomalyKind;
  magnitudePct: number;
  detail: string;
};

const MRR_DROP_THRESHOLD = -0.1;
const SALES_DROP_THRESHOLD = -0.3;

/** Média aritmética; lista vazia → 0 (o chamador trata 0 como "sem base"). */
function avg(values: readonly number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

/**
 * Detector inline (cliente) de anomalias do dashboard /finance, para os badges dos KPIs.
 *
 * Origem: sem issue — commit 498e432c6, 2026-05-04 15:52 +0200 (rota /dashboard/finance).
 *
 * Por quê: roda só sobre a série já carregada no período, sem ida ao servidor — serve para
 * sinal visual imediato. A versão autoritativa é a Function agendada `detectAnomalies`
 * (`apps/functions/src/aggregations/detectAnomalies.ts`, Sprint 4.7), que grava
 * `/aggregations/alerts/items` e alimenta o e-mail crítico. Este comentário dizia "virá em
 * 4.7" — ela já existe; corrigido em 2026-10-03 (#798).
 *
 * Contrato: `MRR_DROP` = MRR USD do último dia ≤ −10 % vs média dos 7 dias anteriores;
 * `SALES_DROP` = tenants criados ≤ −30 % vs a mesma média; `NET_NEW_NEGATIVE_3D` = Net New
 * MRR negativo nos 3 últimos dias (`magnitudePct` fixo em −1, não é percentual). Série com
 * menos de 2 dias → `[]`. `detail` é texto pt-BR fixo, fora do i18n.
 *
 * Usado por: `KpiCards` e `FinanceDashboardPage` (`apps/admin`).
 *
 * Dicas: os limiares (−10 %/−30 %) espelham os do servidor, mas o servidor exige base de
 * ≥ 10 vendas/dia (`SALES_BASELINE_MIN`) e este hook não — num período de poucas vendas o
 * badge `SALES_DROP` pode aparecer aqui sem alerta correspondente no servidor. Ao mudar um
 * limiar, mudar nos dois lugares.
 */
export function useAnomaly(series: readonly DailyAggregate[]): Anomaly[] {
  return useMemo(() => detect(series), [series]);
}

/** Aplica as três regras do contrato de `useAnomaly` sobre a série ordenada por dia. */
function detect(series: readonly DailyAggregate[]): Anomaly[] {
  if (series.length < 2) return [];
  const out: Anomaly[] = [];
  const last = series[series.length - 1];
  if (!last) return out;
  const baseline7d = series.slice(Math.max(0, series.length - 8), series.length - 1);

  const mrrAvg = avg(baseline7d.map((d) => d.mrr.usdEquivalentCents));
  if (mrrAvg > 0) {
    const drop = (last.mrr.usdEquivalentCents - mrrAvg) / mrrAvg;
    if (drop <= MRR_DROP_THRESHOLD) {
      out.push({
        kind: 'MRR_DROP',
        magnitudePct: drop,
        detail: 'MRR caiu mais de 10% no último dia vs média 7d.',
      });
    }
  }

  const salesAvg = avg(baseline7d.map((d) => d.tenants.createdToday));
  if (salesAvg > 0) {
    const drop = (last.tenants.createdToday - salesAvg) / salesAvg;
    if (drop <= SALES_DROP_THRESHOLD) {
      out.push({
        kind: 'SALES_DROP',
        magnitudePct: drop,
        detail: 'Vendas/dia caíram mais de 30% no último dia vs média 7d.',
      });
    }
  }

  if (series.length >= 3) {
    const lastThree = series.slice(-3);
    if (lastThree.every((d) => d.netNewMrr.netUsdCents < 0)) {
      out.push({
        kind: 'NET_NEW_NEGATIVE_3D',
        magnitudePct: -1,
        detail: 'MRR Net New negativo 3 dias consecutivos.',
      });
    }
  }

  return out;
}
